import { HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { DocumentMetadata } from 'src/app/models/document-metadata';
import { FileUploadEventModel } from 'src/app/models/file-upload-event.model';
import { DocumentService } from 'src/app/services/document/document-service.service';
import { TrainingProgramService } from 'src/app/services/training-program/training-program.service';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    standalone: false
})
export class FileUploadComponent implements OnInit {
  @Input() maxFileNumber: number | null = null
  @Input() sectionName: string = ""
  @Input() courseApprovalId: string = ""
  @Input() displayMode: string = "Upload" /* Available modes: Upload, Download, DownloadUpload, ViewOnly UploadNoDelete*/
  @Input() displayErrors: boolean = false
  @Input() programStatusId: number
  @Input() soseApplicationId: string = ""
  @Output() documentUploadEvent: EventEmitter<any> = new EventEmitter()
  uploadedDocList: any[] = []

  invalidFile = ""
  invalidReason = ""
  chunkSize = 30 * this.documentService.MB;
  currentMetadatId: string = '';
  currentMetadataName: string = '';
  disableIfUnderReview: boolean = false;
  version: number = 0;
  documentStatus: number = 0;

  phaseId: number = -1;
  tpAppUtils = TrainingProgramApplicationUtils;
  acceptedFiles: string;

  constructor(@Inject('DocumentService') public documentService: DocumentService,
    @Inject('TrainingProgramService') public trainingProgramService: TrainingProgramService) { }

  ngOnInit(): void {
    this.checkIfActionShouldBeDisabled();
    this.acceptedFiles = ".docx, .jpeg, .jpg, .pdf, .png";
    if (this.soseApplicationId) {
      this.acceptedFiles += ", .doc";
    } else {
      this.acceptedFiles += ", .xlsx, .ppt, .pptx, .gif, .mp4";
    }

    if (this.courseApprovalId) {
      this.trainingProgramService.GetCoursApprovalNextChangeRequestVersion(this.courseApprovalId).subscribe(x => this.version = x);
    }
  }

  chooseFile(event) {
    let reader = new FileReader()
    if (event.target.files && event.target.files.length > 0) {
      let file = event.target.files[0]
      let fileType = file.type
      let extension = file.name.split('.')[file.name.split('.').length - 1].toLowerCase()
      let sizePlus = file.size

      switch (extension) {
        case "doc":
          fileType = 'application/msword'
          break
        case "docx":
          fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          break
        case "gif":
          fileType = 'image/gif'
          break
        case "mp4":
          fileType = 'application/mp4'
          break
        case "png":
          fileType = 'image/png'
          break
        case "jpg":
          fileType = 'image/jpg'
          break
        case "jpeg":
          fileType = 'image/jpeg'
          break
        case "xlsx":
          fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          break
        case "pdf":
          fileType = 'application/pdf'
          break
        case "xls":
          fileType = 'application/vnd.ms-excel'
          break
        case "ppt":
          fileType = 'application/vnd.ms-powerpoint'
          break
        case "pptx":
          fileType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
          break
        default:
          fileType = 'unsupported'
          break
      }

      //TODO make sure all file types are here
      const validFileTypes = this.soseApplicationId?['application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpg',
        'image/jpeg',
        'application/pdf'] : ['application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/gif',
        'application/mp4',
        'image/png',
        'image/jpg',
        'image/jpeg',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf',
        'application/vnd.ms-excel',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation']

      if (!validFileTypes.includes(fileType)) {
        //bad file type
        this.invalidFile = file.name
        this.invalidReason = 'type'
      } else if (sizePlus > this.documentService.GB) {
        //the spoon is too big
        this.invalidFile = file.name
        this.invalidReason = 'size'

      } else if (this.uploadedDocList.find(d => d.fileName.toLowerCase() == file.name.toLowerCase()) && this.currentMetadataName.toLowerCase() != file.name.toLowerCase()) {
        this.invalidFile = file.name
        this.invalidReason = 'name'

      } else {

        let index = 0;
        // if this is a NEW file, this.currentMetadataName = ''
        //    and Version -> 2, DocumentStatus -> 2
        // if this is an EXISTING file, this.currentMetadataName = file name
        //    the existing file: Version NULL -> 2, DocumentStatus  NULL -> NULL
        //    the replacing file: Version -> 2, DocumentStatus -> 1
        if (this.currentMetadataName != '') {
          index = this.uploadedDocList.findIndex(x => x.fileName == this.currentMetadataName);

          let prevVersion = Object.assign({}, this.uploadedDocList[index])
          this.uploadedDocList[index] = new DocumentMetadata(file.name, new Date(), file.size, '', this.version);
          this.uploadedDocList[index].prevVersion = prevVersion;
          this.documentStatus = 1;
        }
        else {
          this.uploadedDocList.push(new DocumentMetadata(file.name, new Date(), file.size, '', this.version))
          index = this.uploadedDocList.length - 1;
          this.documentStatus = 2;
        }

        this.documentUploadEvent.emit()

        this.getLargestDenominator(file.size, index)

        if (file instanceof Blob) {
          reader.readAsDataURL(file)
          reader.onload = async () => {

            this.documentUploadEvent.emit()
            //chunk this file into separate 50mb segments
            let tempStatus
            let previousMetadata = this.currentMetadatId == '' || this.currentMetadatId == undefined ? null : this.currentMetadatId;
            this.uploadFileAsChunk(file, tempStatus, index, 0, null, previousMetadata)
          }
        }
        this.invalidFile = ""
      }
    }
  }

  uploadFileAsChunk(file, tempStatus, index, offset, uploadId, previousVerId?): any {
    const chunk = file.slice(offset, offset + this.chunkSize);
    const isFileSmallerThanChunk = file.size <= this.chunkSize

    const formData = new FormData();
    formData.append('file', chunk, file.name);

    let ownerId = this.courseApprovalId;
    let soseMode = false;
    if (this.soseApplicationId) {
      // SOSE Mode
      soseMode = true;
      ownerId = this.soseApplicationId;
      this.phaseId = 0;
      this.programStatusId = this.tpAppUtils.SoseApplicationStatusIncomplete;
      if(this.displayMode == 'UploadNoDelete'){
        this.documentStatus = 3;
      }
    } else {
      // Training Provider mode
      const statusName = sessionStorage.getItem('triageStatus') ?? 'TRIAGE';
      this.phaseId = this.tpAppUtils.getPhaseIdFromTriageStatus(statusName);
    }
    this.uploadedDocList[index].subscription = this.documentService.Upload(formData, ownerId, this.sectionName, previousVerId, offset, (offset >= file.size - this.chunkSize) && !isFileSmallerThanChunk,
         uploadId, (this.uploadedDocList[index].fileSize * this.uploadedDocList[index].unitOfMeasurementValue) > this.chunkSize, this.phaseId, this.programStatusId, this.version, this.documentStatus, soseMode).subscribe(event => {

      if (event.type === HttpEventType.UploadProgress) {
        let roundedProg = this.getRoundedTo2Places(event.loaded / this.uploadedDocList[index].unitOfMeasurementValue)
        this.uploadedDocList[index].progress = Math.min(this.getRoundedTo2Places(((roundedProg > this.uploadedDocList[index].fileResponseDifference) ? roundedProg - this.uploadedDocList[index].fileResponseDifference : 0) + offset / this.uploadedDocList[index].unitOfMeasurementValue), this.uploadedDocList[index].fileSize)
      }
      if (event.type === HttpEventType.Response) {
        //save the stream id for backend document upload
        if (!uploadId) uploadId = event.body.message
        tempStatus = event.status == 200 && event.body.code == '200'

        //if failure
        if (!tempStatus) {
          this.uploadedDocList[index].loading = false
          this.uploadedDocList[index].success = false
          this.documentUploadEvent.emit()
        }
        //final pass, set the view model to reflect status
        else if (offset >= file.size - this.chunkSize) {
          this.uploadedDocList[index].loading = false
          this.uploadedDocList[index].success = event.status == 200
          this.uploadedDocList[index].uploadSuccess = event.status == 200
          this.uploadedDocList[index].metadataId = event.body.documentMetadataId
          this.documentUploadEvent.emit(new FileUploadEventModel('success', event.body.documentMetadataId, file.name))
        }
        else {
          offset += this.chunkSize
          this.uploadFileAsChunk(file, tempStatus, index, offset, uploadId, previousVerId)
        }
      }
    }, err => {
      this.uploadedDocList[index].loading = false
      this.uploadedDocList[index].success = false
      this.documentUploadEvent.emit()
    })
  }

  getLargestDenominator(fileSize: number, index: number) {
    if (fileSize >= 1073741824) {
      this.uploadedDocList[index].total = this.getRoundedTo2Places(fileSize / 1073741824)
      this.uploadedDocList[index].unitOfMeasurementString = 'GB'
      this.uploadedDocList[index].unitOfMeasurementValue = 1073741824
    } else if (fileSize >= 1048576) {
      this.uploadedDocList[index].total = this.getRoundedTo2Places(fileSize / 1048576)
      this.uploadedDocList[index].unitOfMeasurementString = 'MB'
      this.uploadedDocList[index].unitOfMeasurementValue = 1048576
    } else {
      this.uploadedDocList[index].total = this.getRoundedTo2Places(fileSize / 1024)
      this.uploadedDocList[index].unitOfMeasurementString = 'KB'
      this.uploadedDocList[index].unitOfMeasurementValue = 1024
    }

    this.uploadedDocList[index].fileSize = this.getRoundedTo2Places(this.uploadedDocList[index].fileSize / this.uploadedDocList[index].unitOfMeasurementValue)
    this.uploadedDocList[index].fileResponseDifference = this.uploadedDocList[index].total - this.uploadedDocList[index].fileSize
  }

  getRoundedTo2Places(number) {
    return Math.round((number + Number.EPSILON) * 100) / 100
  }

  removeDocument(doc, index) {
    let fullyErase = true
    if (doc.subscription) {
      doc.subscription.unsubscribe()

      if (this.currentMetadatId) {
        this.uploadedDocList[index] = this.uploadedDocList[index].prevVersion
        fullyErase = false
      }
    }

    if (doc.metadataId != '') {
      const soseMode = this.soseApplicationId ? true : false;

      this.documentService.DeleteDocumentMetadata(doc.metadataId, soseMode).subscribe(successFlag => {
        if (fullyErase) this.uploadedDocList = this.uploadedDocList.filter(x => x.fileName != doc.fileName);
        this.documentUploadEvent.emit();
      });
    }
    else {
      if (fullyErase) this.uploadedDocList = this.uploadedDocList.filter(x => x.fileName != doc.fileName);
      this.documentUploadEvent.emit();
    }
  }

  downloadDocument(metadataId: string) {
    var downloadingFile = this.uploadedDocList.find(d => d.metadataId.toLowerCase() == metadataId);
    downloadingFile.downloading = true
    this.documentService.GetFilesize(metadataId, downloadingFile.Version).subscribe(filesize => this.recursiveDownload(metadataId, 0, filesize, null, downloadingFile.Version));
  }

  recursiveDownload(metadataId: string, offset: number, filesize: number, doc?: any, version?: number) {
    if (metadataId != '') {

      //should get file size first - then can iterate on that
      this.documentService.Download(metadataId, offset, version).subscribe(response => {
        if (!doc) doc = { filesize: response.body!.size, blobs: [response.body] }
        else {
          doc.filesize += response.body!.size
          doc.blobs.push(response.body)
        }

        //@ts-ignore
        if (doc.filesize >= filesize) {
          var fileName: string | undefined = response.headers.get('ContentDisposition')?.split('=')[1];
          let downloadLink = document.createElement('a');

          let blobMerge = new Blob(doc.blobs, { type: doc.blobs[0].type })
          //@ts-ignore
          downloadLink.href = window.URL.createObjectURL(blobMerge);
          downloadLink.setAttribute('download', fileName!);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          window.URL.revokeObjectURL(downloadLink.href);
          this.uploadedDocList.find(d => d.metadataId.toLowerCase() == metadataId).downloading = false
        } else this.recursiveDownload(metadataId, doc.filesize, filesize, doc, version)
      });
    }
  }

  hasNoFiles(considerLoading: boolean) {
    return this.uploadedDocList.filter(x => x.success || (considerLoading && x.loading && x.failure != true)).length == 0
  }

  checkIfActionShouldBeDisabled() {
    if (this.programStatusId) { 
      this.disableIfUnderReview = [
        this.tpAppUtils.NotSubmitted, 
        this.tpAppUtils.DapIssued, 
        this.tpAppUtils.DeskDapIssued, 
        this.tpAppUtils.FieldDapIssued, 
        this.tpAppUtils.CPOApproved, 
        this.tpAppUtils.ProactiveChangesSubmitted,
        this.tpAppUtils.ProQADapIssued,
        this.tpAppUtils.TrainingProgramApproved,
        this.tpAppUtils.DapIssuedForFieldAssessment].includes(this.programStatusId) ? false : true; 
    }
  }
}
